import { useCallback } from 'react';
import { AuthorizationAPI } from '../apis/AuthorizationAPI';
import { saveToCache, getFromCache } from '../utils/indexedDBCache';
import Cookies from 'js-cookie';
const POLICIES_CACHE_KEY = 'policies_data';
// Module-level cache and request tracking
let cachedPolicies = [];
let ongoingRequest = null;
/**
 * Hook to get policies with IndexedDB caching
 * Caching strategy: module cache → sessionStorage → IndexedDB → API
 * Prevents duplicate API calls across components
 */
export const usePoliciesData = () => {
    const getPolicies = useCallback(async () => {
        // 1. Return module-level cache if available
        if (cachedPolicies.length > 0) {
            return cachedPolicies;
        }
        // 2. Check if user is authenticated
        const accessToken = Cookies.get('access_token');
        if (!accessToken) {
            return [];
        }
        // 3. Check sessionStorage
        const sessionPolicies = sessionStorage.getItem('policy');
        if (sessionPolicies) {
            try {
                const parsed = JSON.parse(sessionPolicies);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    cachedPolicies = parsed;
                    return parsed;
                }
            }
            catch (error) {
                console.warn('Failed to parse policies from sessionStorage:', error);
            }
        }
        // 4. Check IndexedDB cache
        try {
            const cachedFromDB = await getFromCache(POLICIES_CACHE_KEY);
            if (cachedFromDB && cachedFromDB.length > 0) {
                cachedPolicies = cachedFromDB;
                sessionStorage.setItem('policy', JSON.stringify(cachedFromDB));
                return cachedFromDB;
            }
        }
        catch (error) {
            console.warn('Failed to retrieve policies from IndexedDB:', error);
        }
        // 5. Return ongoing request if exists (prevent duplicate API calls)
        if (ongoingRequest) {
            return ongoingRequest;
        }
        // 6. Fetch from API
        const requestPromise = (async () => {
            try {
                const response = await AuthorizationAPI.getPolicy();
                let fetchedPolicies = [];
                if (response.resource) {
                    for (const singleEnabledPolicy of response.resource) {
                        if (singleEnabledPolicy.name) {
                            fetchedPolicies.push(singleEnabledPolicy);
                        }
                    }
                }
                // Store in module cache
                cachedPolicies = fetchedPolicies;
                // Store in sessionStorage and IndexedDB
                sessionStorage.setItem('policy', JSON.stringify(fetchedPolicies));
                try {
                    await saveToCache(POLICIES_CACHE_KEY, fetchedPolicies);
                }
                catch (error) {
                    console.warn('Failed to cache policies to IndexedDB:', error);
                }
                return fetchedPolicies;
            }
            finally {
                ongoingRequest = null;
            }
        })();
        ongoingRequest = requestPromise;
        return requestPromise;
    }, []);
    return getPolicies;
};
/**
 * Clear the module-level cache (useful for logout)
 */
export const clearPoliciesCache = () => {
    cachedPolicies = [];
    ongoingRequest = null;
};
